import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Divider, MenuItem, Select } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import PageLoader from "../../components/PageLoader";
import { toast } from "../../components/Toast";
import { useStore } from "../../store";
import Indicator from "./healthCheck-indicator";
import healthCheckService from "../../services/healthCheck.service";
import AppSidebar from "../../components/AppSidebar/AppSidebar";

type DownTime = {
  date: string;
  duration: string;
};

type HealthDataType = {
  name: string;
  totaluptime: string;
  downtime: DownTime[];
};
export default function HealthCheck() {
  const { dispatch } = useStore();
  const [data, setData] = useState<HealthDataType[]>([]);
  const [selectedDateRange, setSelectedDateRange] = useState(90);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    async function getHealthData() {
      try {
        const healthData = await healthCheckService.getHealthMonitoringData(
          selectedDateRange
        );
        setData(healthData.data);
      } catch (error: any) {
        let errorMsg =
          error?.response?.data?.error?.message ||
          "Failed to fetch health data";
        toast.error(dispatch, errorMsg);
      } finally {
        setIsLoading(false);
      }
    }
    getHealthData();
  }, []);

  const handleDateRangeChange = (event: any) => {
    setSelectedDateRange(event.target.value);
  };

  const currentDate = new Date();
  const pastDates: string[] = [];
  for (let i = selectedDateRange; i >= 0; i--) {
    const date = new Date();
    date.setDate(currentDate.getDate() - i);
    pastDates.push(date.toISOString().split("T")[0]);
  }

  return (
    <Box sx={{ display: "flex" }}>
      <AppSidebar />
      <Box sx={{ flexGrow: 1 }}>
        <Toolbar sx={{ borderBottom: "1px solid #cdcdcd" }} variant="dense">
          <Typography variant="h6" fontWeight={500} component="h1">
            Health
          </Typography>
        </Toolbar>

        <Box sx={{ display: "flex", justifyContent: "center", margin: 5 }}>
          <Grid
            container
            alignItems={"center"}
            bgcolor={"#f5f5f7"}
            width={"80%"}
            height={50}
            paddingLeft={2}
            paddingRight={1}
            borderRadius={"15px"}
            justifyContent={"space-between"}
          >
            <Grid display={"flex"}>
              <CheckCircleIcon color="success" />
              <Typography
                marginLeft={2}
                fontSize={"22px"}
                fontWeight={700}
                lineHeight={"110%"}
              >
                All Services are online
              </Typography>
            </Grid>
            <Grid
              display="flex"
              alignItems="center"
              bgcolor="#fff"
              padding="5px 10px"
              borderRadius="10px"
              height="30px"
              margin="2px 0px"
            >
              <Select
                value={selectedDateRange}
                onChange={handleDateRangeChange}
                sx={{
                  boxShadow: "none",
                  ".MuiOutlinedInput-notchedOutline": { border: 0 },
                  "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                    { border: 0 },
                  "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                    { border: 0 },
                }}
              >
                <MenuItem value="30">30 days ago</MenuItem>
                <MenuItem value="60">60 days ago</MenuItem>
                <MenuItem value="90">90 days ago</MenuItem>
                <MenuItem value="120">120 days ago</MenuItem>
                <MenuItem value="150">150 days ago</MenuItem>
              </Select>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", margin: 5 }}>
          <Paper sx={{ width: "80%", borderRadius: "10px" }}>
            {data?.map((service, index: number) => (
              <Grid
                key={index}
                container
                sx={{ gap: 1 }}
                flexDirection={"column"}
                padding={2}
              >
                <Grid
                  container
                  flexDirection={"row"}
                  justifyContent={"space-between"}
                >
                  <Typography fontSize={"15px"} fontWeight={500}>
                    {service.name}
                  </Typography>
                  <Grid display={"flex"} gap={0.5}>
                    <Typography
                      color={"green"}
                      fontWeight={500}
                      fontSize={"14px"}
                    >
                      {service.totaluptime} uptime
                    </Typography>
                  </Grid>
                </Grid>
                <Divider />
                <Grid
                  container
                  gap={0.5}
                  paddingTop={1}
                  width={"90%"}
                  flexWrap={"nowrap"}
                  style={{ cursor: "pointer" }}
                >
                  {pastDates.map((date, subIndex) => {
                    const downtimeData = service?.downtime?.find(
                      (item: any) => item.date === date
                    );
                    const downTime =
                      downtimeData && downtimeData.duration !== "0s";
                    const duration = downtimeData
                      ? downtimeData.duration
                      : "0s";
                    return (
                      <Indicator
                        key={subIndex}
                        downTime={downTime}
                        duration={duration}
                        date={date}
                        selectedDays={selectedDateRange}
                      />
                    );
                  })}
                </Grid>
              </Grid>
            ))}
            {data.length === 0 && isLoading && <PageLoader />}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
